import { Badge, Button, cn, Dialog, DialogContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Popover, PopoverContent, PopoverTrigger } from '@menage/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Globe, KeyRound, LogOut, MoreHorizontal, Sparkles, type LucideIcon } from 'lucide-react';
import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { setLanguage } from '@/i18n';
import { get, post } from '@/lib/api';
import { useChangePasswordDialog } from '@/features/auth/ChangePasswordCard';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useContentText } from '@/lib/content';
import { useFormat } from '@/lib/format';
import { ErrorBoundary, ListSkeleton } from './common';
import { DevRoleSwitcher } from './DevRoleSwitcher';
import { PublicFooter, PublicHeader, ScrollManager } from '@/features/public/site/layout';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: number;
  /** Shown in the phone bottom bar (max 4, the rest goes under "More"). */
  primary?: boolean;
}

function useVisible() {
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return visible;
}

/** Badge counts refresh slowly and only while the tab is visible (no Realtime, no aggressive polling). */
export function useUnreadMessages(enabled = true) {
  const visible = useVisible();
  return useQuery({
    queryKey: ['chat', 'unread'],
    queryFn: () => get<{ count: number }>('/chat/unread-count'),
    enabled,
    refetchInterval: visible ? 60_000 : false,
    staleTime: 20_000,
  }).data?.count ?? 0;
}

interface NotificationItem {
  id: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

function NotificationBell() {
  const { t } = useTranslation();
  const visible = useVisible();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fmt = useFormat();
  const ct = useContentText();
  const { data, isError } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => get<{ items: NotificationItem[]; unread: number }>('/notifications'),
    refetchInterval: visible ? 90_000 : false,
    staleTime: 30_000,
  });
  const markAll = async () => {
    await post('/notifications/read', {});
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('labels.notifications')} className="relative">
          <Bell />
          {data?.unread ? (
            <span className="absolute right-2 top-2 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-semibold text-destructive-foreground">
              {data.unread > 9 ? '9+' : data.unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(22rem,calc(100vw-2rem))]">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="text-sm font-semibold">{t('labels.notifications')}</p>
          {data?.unread ? (
            <button className="text-xs text-primary hover:underline" onClick={() => void markAll()}>
              {t('labels.markAllRead')}
            </button>
          ) : null}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {isError ? (
            <p role="alert" className="px-4 py-6 text-center text-sm text-muted-foreground">{t('errors.load')}</p>
          ) : !data?.items.length ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t('labels.noNotifications')}</p>
          ) : (
            data.items.map((n) => (
              <button
                key={n.id}
                className={cn('block w-full border-b border-border px-4 py-3 text-left text-sm last:border-0 hover:bg-muted', !n.readAt && 'bg-secondary/40')}
                onClick={async () => {
                  if (!n.readAt) {
                    await post('/notifications/read', { ids: [n.id] });
                    void qc.invalidateQueries({ queryKey: ['notifications'] });
                  }
                  if (n.link) navigate(n.link);
                }}
              >
                <p className={cn(!n.readAt && 'font-medium')}>{ct(n.title)}</p>
                {n.body ? <p className="mt-0.5 line-clamp-2 text-muted-foreground">{ct(n.body)}</p> : null}
                <p className="mt-1 text-xs text-muted-foreground">{fmt.dateTime(n.createdAt)}</p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function UserMenu() {
  const { actor, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const password = useChangePasswordDialog();
  if (!actor) return null;
  const initials = actor.fullName
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('');
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={actor.fullName}>
            <span className="flex size-8 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">{initials}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>
            <span className="block text-sm font-medium text-foreground">{actor.fullName}</span>
            <span className="block">{t(`roles.${actor.role}`)}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setLanguage(i18n.language === 'fr' ? 'en' : 'fr')}>
            <Globe /> {i18n.language === 'fr' ? 'English' : 'Français'}
          </DropdownMenuItem>
          {/* Staff have no profile page: password change from the menu (clients/employees: account page). */}
          {actor.role !== 'CLIENT' && actor.role !== 'EMPLOYEE' ? (
            <DropdownMenuItem onSelect={password.open}>
              <KeyRound /> {password.label}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            onSelect={async () => {
              await logout();
              navigate('/connexion');
            }}
          >
            <LogOut /> {t('actions.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {password.dialog}
    </>
  );
}

function NavLinkItem({ item, onNavigate, compact }: { item: NavItem; onNavigate?: () => void; compact?: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
          isActive ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          compact && 'min-h-10',
        )
      }
    >
      <Icon className="size-[18px] shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge ? <Badge tone="red">{item.badge}</Badge> : null}
    </NavLink>
  );
}

/**
 * Portal layout shared by the client, employee and staff portals:
 * sidebar on desktop, compact top bar + bottom navigation on phones.
 */
export function PortalShell({ nav, title, homeTo }: { nav: NavItem[]; title: string; homeTo: string }) {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  // Browser tab title in the interface language (public pages set their own).
  useEffect(() => {
    document.title = `${title} · ${t('appName')}`;
  }, [title, t]);
  const primary = nav.filter((n) => n.primary).slice(0, 4);
  const secondary = nav.filter((n) => !primary.includes(n));
  const moreActive = secondary.some((n) => location.pathname.startsWith(n.to) && n.to !== homeTo);

  return (
    <div className="min-h-dvh lg:flex">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-lg focus:bg-card focus:p-2">
        {t('labels.skipToContent')}
      </a>
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-border bg-card/70 px-3 py-4 lg:flex">
        <Link to={homeTo} className="mb-6 flex items-center gap-2 px-3 font-semibold">
          <Sparkles className="size-5 text-primary" /> {t('appName')}
        </Link>
        <p className="px-3 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
        <nav className="flex-1 space-y-0.5 overflow-y-auto" aria-label={title}>
          {nav.map((item) => (
            <NavLinkItem key={item.to} item={item} />
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/90 px-3 backdrop-blur sm:px-5">
          <Link to={homeTo} className="flex items-center gap-2 font-semibold lg:hidden">
            <Sparkles className="size-5 text-primary" />
            <span className="truncate">{t('appName')}</span>
          </Link>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <UserMenu />
          </div>
        </header>

        <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-4 pb-28 pt-5 sm:px-6 lg:pb-10">
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<ListSkeleton rows={4} />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>

        {/* Phone bottom navigation */}
        <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 backdrop-blur lg:hidden" aria-label={title}>
          <div className="grid grid-cols-5">
            {primary.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => cn('relative flex min-h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium', isActive ? 'text-primary' : 'text-muted-foreground')}
                >
                  <Icon className="size-5" />
                  <span className="max-w-full truncate px-1">{item.label}</span>
                  {item.badge ? <span className="absolute right-[22%] top-2 size-2 rounded-full bg-destructive" aria-label={String(item.badge)} /> : null}
                </NavLink>
              );
            })}
            {secondary.length ? (
              <button
                className={cn('relative flex min-h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium', moreActive ? 'text-primary' : 'text-muted-foreground')}
                onClick={() => setMoreOpen(true)}
                aria-haspopup="dialog"
              >
                {secondary.some((s) => s.badge) ? <span className="absolute right-[22%] top-2 size-2 rounded-full bg-destructive" /> : null}
                <MoreHorizontal className="size-5" />
                <span>{t('labels.more')}</span>
              </button>
            ) : null}
          </div>
        </nav>
        <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
          <DialogContent title={title}>
            <nav className="space-y-0.5">
              {secondary.map((item) => (
                <NavLinkItem key={item.to} item={item} onNavigate={() => setMoreOpen(false)} />
              ))}
            </nav>
          </DialogContent>
        </Dialog>
      </div>
      <DevRoleSwitcher />
    </div>
  );
}

/** Public pages (site, quote, login, register, legal). */
export function PublicShell({ children }: { children?: ReactNode }) {
  const location = useLocation();
  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#contenu" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-lg focus:bg-card focus:p-2">
        <SkipLabel />
      </a>
      <ScrollManager />
      <PublicHeader />
      <main id="contenu" className="flex-1">
        <ErrorBoundary key={location.pathname}>
          <Suspense fallback={<div className="container py-10"><ListSkeleton /></div>}>{children ?? <Outlet />}</Suspense>
        </ErrorBoundary>
      </main>
      <PublicFooter />
      <DevRoleSwitcher />
    </div>
  );
}

function SkipLabel() {
  const { t } = useTranslation();
  return <>{t('labels.skipToContent')}</>;
}
